import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { EntryErrorModel, EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { DocumentModel } from '@data-layer/models/documents';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { AddressModel, ShipmentModel, ValidationMessageModel } from '@data-layer/models/common';
import { enumDocumentRelatedEntityType, enumDocumentState, enumDocumentType } from '@data-layer/enums/documents';
import { EntityModel } from '@data-layer/models/entities';
import { enumBondRequestState, enumValidationMessageSeverity } from '@data-layer/enums/common';
import { CustomDateByUtcPipe } from '@shared/pipes-shared/pipes';

import {
  ACH_METHOD_NOT_ALLOWED_WITH_PUN,
  ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN,
  ARRIVAL_DATE_MISSING,
  ARRIVAL_DATE_TOO_EARLY,
  BOND_REFERENCE_NUMBER_MALFORMED,
  BOND_REQUEST_AMOUNT_MISSING,
  BOND_REQUEST_NOT_ALLOWED_FOR_INFORMAL_ENTRY,
  BOND_REQUEST_NOT_SENT,
  BOND_REQUEST_PORT_CODE_MISSING,
  BOND_SURETY_CODE_MISSING,
  BOND_TYPE_MISSING,
  BUYER_MISSING,
  CITY_MALFORMED,
  COMPANY_NAME_MALFORMED,
  CONSIGNEE_MISSING,
  COUNTRY_OF_EXPORT_MISSING,
  CURRENCY_IS_NOT_AVAILABLE,
  EXPORT_DATE_LATER_THAN_ARRIVAL_DATE,
  EXPORT_DATE_MISSING,
  FILE_SIZE_TO_BIG_AFTER_SCAN,
  FLIGHT_NUMBER_MISSING,
  FOREIGN_PORT_MISSING,
  FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER,
  FORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_SMALL,
  FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL,
  FREIGHT_COST_LESS_THAN_LINES_COUNT,
  FREIGHT_COST_MISSING,
  GROSS_WEIGHT_MISSING,
  HOUSE_AIR_BILL_NUMBER_MISSING,
  HOUSE_AIR_BILL_NUMBER_TOO_LONG,
  HOUSE_AIR_BILL_PREFIX_MISSING,
  HOUSE_AIR_BILL_PREFIX_WRONG_LENGTH,
  HOUSE_BILL_CARRIER_MALFORMED,
  HOUSE_BILL_CARRIER_MISSING,
  HOUSE_BILL_CARRIER_WRONG_LENGTH,
  HOUSE_BILL_NUMBER_MALFORMED,
  HOUSE_BILL_NUMBER_MISSING,
  HOUSE_BILL_TOO_LONG,
  HTS_COUNTRY_OF_ORIGIN_MISSING,
  HTS_DOES_NOT_EXIST,
  HTS_NUMBER_MISSING,
  HTS_NUMBER_WRONG_LENGTH,
  IDENTIFIER_MALFORMED,
  IMPORTER_MISSING,
  INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG,
  INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
  INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
  INVOICE_MANUFACTURER_MISSING,
  INVOICE_MISSING,
  INVOICE_NUMBER_MALFORMED,
  INVOICE_NUMBER_MISSING,
  INVOICE_OCR_STATE_NOT_REVIEWED,
  IT_DATE_LATER_THAN_ENTRY_DATE,
  IT_DATE_MISSING,
  IT_NUMBER_MALFORMED,
  IT_NUMBER_MISSING,
  LINES_MISSING,
  LOCATION_MISSING,
  MASTER_AIR_BILL_NUMBER_MISSING,
  MASTER_AIR_BILL_NUMBER_TOO_LONG,
  MASTER_AIR_BILL_PREFIX_MISSING,
  MASTER_AIR_BILL_PREFIX_WRONG_LENGTH,
  MASTER_BILL_CARRIER_MALFORMED,
  MASTER_BILL_CARRIER_MISSING,
  MASTER_BILL_CARRIER_WRONG_LENGTH,
  MASTER_BILL_NUMBER_MALFORMED,
  MASTER_BILL_NUMBER_MISSING,
  MASTER_BILL_TOO_LONG,
  MISSING_ADDRESS,
  MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING,
  MISSING_ATTACHED_DOCUMENT_INVOICE,
  MISSING_ATTACHED_DOCUMENT_POA,
  MISSING_ATTACHED_DOCUMENT_PUN,
  MISSING_CANADA_STATE,
  MISSING_CITY,
  MISSING_COMPANY_NAME,
  MISSING_COUNTRY,
  MISSING_DATE_OF_BIRTH,
  MISSING_IDENTIFIER,
  MISSING_IDENTIFIER_QUALIFIER,
  MISSING_POSTAL_CODE,
  MISSING_STATE,
  PAYER_UNIT_NUMBER_MALFORMED,
  PAYER_UNIT_NUMBER_MISSING,
  PORT_OF_ENTRY_MISSING,
  PORT_OF_UNLADING_MISSING,
  POSTAL_CODE_MALFORMED,
  QUALIFIER_MALFORMED,
  QUANTITY1_MISSING,
  QUANTITY2_MALFORMED,
  QUANTITY3_MALFORMED,
  QUANTITY_MISSING,
  QUANTITY_UNIT_MISSING,
  SCAN_FAIL_FILE,
  SELLER_MISSING,
  SHIPMENT_MISSING,
  SINGLE_BOND_REQUEST_FORBIDDEN,
  SPECIAL_CHAPTER_HTS_DOES_NOT_EXIST,
  SPECIAL_CHAPTER_MISSING,
  STATE_OF_DESTINATION_MISSING,
  STREET_ADDRESS_MALFORMED,
  TRANSPORTATION_MODE_MISSING,
  TRIP_MISSING,
  UNIT1_MISSING,
  VALUE_MISSING,
  VESSEL_NAME_MISSING,
  VESSEL_NAME_TOO_LONG,
  WRONG_IDENTIFIER_FORMAT,
  WRONG_IDENTIFIER_LENGTH,
  HTS_NOT_VERIFIED,
} from '@constants/entity-validation-messages';
import { enumEsEditPageType } from '@enum/enumEsEditPageType';
import { EntrySummaryService } from './entry-summary.service';
import { IEsValidationData } from '@interfaces/es-validation-data';
import { IEsValidationDataFields } from '@interfaces/es-validation-data-fields';
import { isCbpAssignedNumberImporter } from '@helpers/importer.helper';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { ManagementCompanyModel, ManagementEntrySummaryModel } from '@data-layer/models/management';
import { CHINA_COUNTRY_CODE, ENTERED_VALUE_CHINA_LIMIT, ENTERED_VALUE_TOTAL_LIMIT } from '@constants/constants';
import { MaskPipe } from 'ngx-mask';
import { HTS_MASK } from '@helpers/custom-validator';
import { CompaniesService } from './companies.service';
import { CompanyBondModel, CompanyModel } from '@data-layer/models/companies';
import { flatten } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';

@Injectable({
  providedIn: 'root',
})
export class EntrySummaryValidationService {
  private readonly brokerValidationStates: enumEntrySummaryState[] = [
    enumEntrySummaryState.NOT_SENT,
    enumEntrySummaryState.BROKER_PROCESSING_FAILED,
    enumEntrySummaryState.NOT_VALID_PROVIDER,
  ];

  constructor(
    private entrySummaryService: EntrySummaryService,
    private translateService: TranslateService,
    private dateByUtcPipe: CustomDateByUtcPipe,
    private maskPipe: MaskPipe,
    private companiesService: CompaniesService,
  ) {}

  public getEntryValidationErrorData(
    entrySummary: EntrySummaryModel,
    attachedDocuments: DocumentModel[],
    company?: CompanyModel | ManagementCompanyModel,
  ): Observable<IEsValidationData> {
    if (
      entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      !this.brokerValidationStates.includes(entrySummary.state)
    ) {
      return of({
        editPageType: null,
        data: null,
        messages: [],
      });
    }
    return this.entrySummaryService.getEntrySummaryEnteredValue(entrySummary).pipe(
      map((totalEnteredValue: number) => {
        const entryMessages = EntrySummaryValidationHelper.getValidationMessages(
          entrySummary,
          entrySummary.transportationMode,
          true,
          [],
          true,
        );
        return UtilsHelper.concat<ValidationMessageModel>(
          this.getAttachedDocumentMessages(entrySummary, attachedDocuments),
          entryMessages,
          this.getShipmentTypeMessages(entrySummary, totalEnteredValue),
          this.getDutiesPaymentTypeMessages(entrySummary, company),
          this.getImporterMessages(entrySummary),
        );
      }),
      switchMap((entryMessages) => {
        return forkJoin(
          (entrySummary.shipments || []).map((shipment) =>
            this.getShipmentValidationMessagesErrorData(shipment, entrySummary.transportationMode),
          ),
        ).pipe(
          map((shipmentMessages) => {
            return UtilsHelper.concat<ValidationMessageModel>(entryMessages, flatten(shipmentMessages));
          }),
        );
      }),
      map((messages) => {
        const data = messages && messages.length ? this.getValidationErrorLinkData(messages[0], entrySummary) : null;
        return {
          messages,
          ...data,
        };
      }),
    );
  }

  public getErrorParams(message: EntryErrorModel, entrySummary: EntrySummaryModel | ManagementEntrySummaryModel) {
    const entity = this.getEntityFromError(message.entityId, entrySummary);
    switch (message.errorCode) {
      case 'HTS_NOT_FOUND':
        const lineItem: LineModel = entity;
        const htsNumbers: string[] = [];
        if (lineItem.htsNumber) {
          htsNumbers.push(lineItem.htsNumber);
        }

        if (lineItem.specialChapterHtsNumber) {
          htsNumbers.push(lineItem.specialChapterHtsNumber);
        }

        return { htsNumber: htsNumbers.map((item) => this.maskPipe.transform(item, HTS_MASK)).join(', ') };
        break;

      case 'MASTER_CARRIER_NOT_FOUND':
        const mShipment: ShipmentModel = entity;
        if (mShipment.masterBill && mShipment.masterBill.carrier) {
          return { carrier: mShipment.masterBill.carrier };
        }

        return { carrier: '' };
        break;

      case 'HOUSE_CARRIER_NOT_FOUND':
        const hShipment: ShipmentModel = entity;
        if (hShipment.houseBill && hShipment.houseBill.carrier) {
          return { carrier: hShipment.houseBill.carrier };
        }

        return { carrier: '' };
        break;

      case 'CARRIER_TRANSPORTATION_MODE_MISMATCH':
        const shipment: ShipmentModel = entrySummary.shipments[0];
        if (shipment.masterBill && shipment.masterBill.carrier) {
          return { carrier: shipment.masterBill.carrier };
        }

        return { carrier: '' };
        break;

      case 'LOCATION_TRANSPORTATION_MODE_MISMATCH':
        return { mode: enumTransportationMode[entrySummary.transportationMode] };

        break;

      case 'MASTER_BILL_NOT_UNIQUE':
        let masterName = '';

        masterName = this.translateService.instant(
          entrySummary.transportationMode === enumTransportationMode.AIR
            ? 'ENTRY_ERROR_MESSAGES.AIR_WAYBILL_NUMBER'
            : 'ENTRY_ERROR_MESSAGES.BILL_OF_LADING_NUMBER',
        );

        return {
          name: masterName,
          number: <ShipmentModel>entity.masterBillNumber || '',
        };
        break;

      case 'HOUSE_BILL_NOT_UNIQUE':
        let houseName = '';

        houseName = this.translateService.instant(
          entrySummary.transportationMode === enumTransportationMode.AIR
            ? 'ENTRY_ERROR_MESSAGES.AIR_WAYBILL_NUMBER'
            : 'ENTRY_ERROR_MESSAGES.BILL_OF_LADING_NUMBER',
        );

        return {
          name: houseName,
          number: <ShipmentModel>entity.houseBillNumber || '',
        };
        break;

      case 'TARIFF_NOT_ACTIVE':
        return {
          htsCode: entity ? <LineModel>entity.htsNumber : '' || '',
          date: entrySummary.arrivalDate ? this.dateByUtcPipe.transform(entrySummary.arrivalDate) : '' || '',
        };
        break;

      case 'DUPLICATE_HTS':
      case 'HTS_COUNTRY_MISMATCH':
        return { htsCode: entity ? <LineModel>entity.htsNumber : '' || '' };
        break;

      default:
        break;
    }
    return {};
  }

  public isBlDetailsEmpty(shipment: ShipmentModel): boolean {
    const shipmentValidationMessages = shipment.validationMessages;
    const consigneeValidationMessages = shipment.consignee?.validationMessages;
    const sellerValidationMessages = shipment.consignee?.validationMessages;

    const emptyBlValidationMessages = [
      MASTER_BILL_NUMBER_MISSING,
      MASTER_BILL_CARRIER_MISSING,
      QUANTITY_MISSING,
      QUANTITY_UNIT_MISSING,
      SELLER_MISSING,
      CONSIGNEE_MISSING,
      FREIGHT_COST_MISSING,
    ];

    const emptyConsigneeValidationMessages = [MISSING_IDENTIFIER, MISSING_COMPANY_NAME, MISSING_ADDRESS];

    const emptySellerValidationMessages = [MISSING_COMPANY_NAME, MISSING_ADDRESS];

    let totalMatchedEmptyBlValidationMessages: number = 0;

    shipmentValidationMessages.forEach((validationMessage) => {
      if (emptyBlValidationMessages.includes(validationMessage.text)) {
        totalMatchedEmptyBlValidationMessages++;
      }
    });

    const isShipmentValidationMessagesWithConsigneeMissing = shipmentValidationMessages?.find((value) => value.text === CONSIGNEE_MISSING);
    if (!isShipmentValidationMessagesWithConsigneeMissing) {
      const totalEmptyConsigneeValidationMessages: number = emptyConsigneeValidationMessages.length;
      let totalMatchedEmptyConsigneeValidationMessages: number = 0;

      consigneeValidationMessages.forEach((validationMessage) => {
        if (emptyConsigneeValidationMessages.includes(validationMessage.text)) {
          totalMatchedEmptyConsigneeValidationMessages++;
        }
      });

      const isConsigneeMissing = totalEmptyConsigneeValidationMessages === totalMatchedEmptyConsigneeValidationMessages;
      if (isConsigneeMissing) {
        totalMatchedEmptyBlValidationMessages++;
      }
    }

    const isShipmentValidationMessagesWithSellerMissing = shipmentValidationMessages?.find((value) => value.text === SELLER_MISSING);
    if (!isShipmentValidationMessagesWithSellerMissing) {
      const totalEmptySellerValidationMessages: number = emptySellerValidationMessages.length;
      let totalMatchedEmptySellerValidationMessages: number = 0;

      sellerValidationMessages.forEach((validationMessage) => {
        if (emptySellerValidationMessages.includes(validationMessage.text)) {
          totalMatchedEmptySellerValidationMessages++;
        }
      });

      const isSellerMissing = totalEmptySellerValidationMessages === totalMatchedEmptySellerValidationMessages;
      if (isSellerMissing) {
        totalMatchedEmptyBlValidationMessages++;
      }
    }

    const totalEmptyBlValidationMessages: number = emptyBlValidationMessages.length;

    return totalEmptyBlValidationMessages === totalMatchedEmptyBlValidationMessages;
  }

  public isQuickFixShipmentMainDetailsInvalid(entrySummary: EntrySummaryModel, shipment: ShipmentModel): boolean {
    const esValidationMessages = entrySummary.validationMessages;
    const shValidationMessages = shipment.validationMessages;

    return !![...esValidationMessages, ...shValidationMessages].find((error) => {
      switch (error.text) {
        case ARRIVAL_DATE_MISSING:
        case ARRIVAL_DATE_TOO_EARLY:
        case LOCATION_MISSING:
        case PORT_OF_ENTRY_MISSING:
        case PORT_OF_UNLADING_MISSING:
        case TRIP_MISSING:
        case FLIGHT_NUMBER_MISSING:
        case VESSEL_NAME_MISSING:
        case VESSEL_NAME_TOO_LONG:
        case STATE_OF_DESTINATION_MISSING:
        case EXPORT_DATE_MISSING:
        case EXPORT_DATE_LATER_THAN_ARRIVAL_DATE:
        case FOREIGN_PORT_MISSING:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixShipmentTypeDetailsInvalid(entrySummary: EntrySummaryModel, bonds: CompanyBondModel[]): boolean {
    const entryType: enumEntrySummaryType = entrySummary.type;
    return entryType === enumEntrySummaryType.FORMAL && !bonds?.length;
  }

  public isQuickFixBlDetailsInvalid(entrySummary: EntrySummaryModel, shipment: ShipmentModel) {
    const shValidationMessages = shipment.validationMessages;

    return !!shValidationMessages.find((error) => {
      switch (error.text) {
        case MASTER_BILL_NUMBER_MISSING:
        case MASTER_AIR_BILL_NUMBER_MISSING:
        case MASTER_BILL_CARRIER_MISSING:
        case MASTER_AIR_BILL_PREFIX_MISSING:
        case MASTER_BILL_CARRIER_WRONG_LENGTH:
        case MASTER_AIR_BILL_PREFIX_WRONG_LENGTH:
        case MASTER_BILL_TOO_LONG:
        case MASTER_AIR_BILL_NUMBER_TOO_LONG:
        case HOUSE_BILL_CARRIER_MISSING:
        case HOUSE_AIR_BILL_PREFIX_MISSING:
        case HOUSE_BILL_NUMBER_MISSING:
        case HOUSE_AIR_BILL_NUMBER_MISSING:
        case HOUSE_BILL_CARRIER_WRONG_LENGTH:
        case HOUSE_AIR_BILL_PREFIX_WRONG_LENGTH:
        case HOUSE_BILL_TOO_LONG:
        case HOUSE_AIR_BILL_NUMBER_TOO_LONG:
        case MASTER_BILL_CARRIER_MALFORMED:
        case MASTER_BILL_NUMBER_MALFORMED:
        case HOUSE_BILL_CARRIER_MALFORMED:
        case HOUSE_BILL_NUMBER_MALFORMED:
        case QUANTITY_MISSING:
        case QUANTITY_UNIT_MISSING:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixSellerAndConsigneeDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment.validationMessages;
    const cnValidationMessages = shipment?.consignee?.address?.validationMessages;
    const seValidationMessages = shipment?.seller?.address?.validationMessages;
    const byValidationMessages = shipment?.buyer?.address?.validationMessages;

    // Add entity-level validation messages for consignee and buyer
    const cnEntityValidationMessages = shipment?.consignee?.validationMessages || [];
    const byEntityValidationMessages = shipment?.buyer?.validationMessages || [];

    const addressValidationMessages = {
      ...cnValidationMessages,
      ...seValidationMessages,
      ...byValidationMessages,
    };

    const addressValidationMessagesArr: ValidationMessageModel[] = Object.keys(addressValidationMessages).map(
      (key) => addressValidationMessages[key],
    );

    // Direct field validation for identifier (independent of backend validationMessages)
    const isBuyerIdentifierMissing = shipment?.buyer && !shipment.buyer.entityIdentifier;
    const isConsigneeIdentifierMissing = shipment?.consignee && !shipment.consignee.entityIdentifier;

    if (isBuyerIdentifierMissing || isConsigneeIdentifierMissing) {
      return true;
    }

    return !![...shValidationMessages, ...addressValidationMessagesArr, ...cnEntityValidationMessages, ...byEntityValidationMessages].find(
      (error) => {
        switch (error.text) {
          case SELLER_MISSING:
          case CONSIGNEE_MISSING:
          case BUYER_MISSING:
          case MISSING_ADDRESS:
          case MISSING_CITY:
          case MISSING_POSTAL_CODE:
          case MISSING_STATE:
          case MISSING_IDENTIFIER:
          case MISSING_IDENTIFIER_QUALIFIER:
            return true;
          default:
            return false;
        }
      },
    );
  }

  public isQuickFixTosDetailsInvalid(shipment: ShipmentModel) {
    const shValidationMessages = shipment.validationMessages;

    return !!shValidationMessages.find((error) => {
      switch (error.text) {
        case FREIGHT_COST_MISSING:
        case FREIGHT_COST_LESS_THAN_LINES_COUNT:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixInvoiceDetailsInvalid(invoice: InvoiceModel): boolean {
    // #13555: the backend reports a missing country of export on the lines, not on the invoice, and the line step
    // deliberately skips that tag (the field belongs to the invoice step) - so check the invoice field itself
    if (!invoice.countryOfExportId) {
      return true;
    }

    const invoiceValidationMessages = invoice.validationMessages;

    return !!invoiceValidationMessages.find((error) => {
      switch (error.text) {
        case INVOICE_NUMBER_MISSING:
        case INVOICE_NUMBER_MALFORMED:
        case COUNTRY_OF_EXPORT_MISSING:
          return true;
        default:
          return false;
      }
    });
  }

  public isQuickFixManufacturerDetailsInvalid(invoice: InvoiceModel) {
    const invoiceValidationMessages = invoice.validationMessages;

    return !!invoiceValidationMessages.find((error) => {
      switch (error.text) {
        case INVOICE_MANUFACTURER_MISSING:
          return true;
        default:
          return false;
      }
    });
  }

  /**
   * #13415 — the frontend-only "missing / not scanned document" rules WITHOUT the state short-circuit of
   * getAttachedDocumentMessages. The customs-broker review page (state BROKER_REVIEW_PENDING, excluded from
   * brokerValidationStates) applies the same rules to gate its Confirm button and to render the missing-document
   * placeholders. Required set: POA (entry level), PUN approval when duties are paid by ACH, a Bill of Lading per
   * shipment unless the mode is Truck, an Invoice file per invoice; MANUAL submissions only need the POA while a
   * bond request is in CREATED state.
   */
  public getRequiredDocumentMessages(entrySummary: EntrySummaryModel, attachedDocuments: DocumentModel[]): ValidationMessageModel[] {
    const notUploadedDocuments = this.getNotUploadedDocuments(attachedDocuments);
    const invalidAfterScanDocuments = this.getInvalidAfterScanDocuments(attachedDocuments);
    const hasPoaAttachedDocument: boolean = !!attachedDocuments.find(
      (doc) =>
        doc.relatedEntityId === entrySummary.id &&
        [enumDocumentType.POWER_OF_ATTORNEY, enumDocumentType.POWER_OF_ATTORNEY_TRUCKER].includes(doc.type) &&
        doc.relatedEntityType === enumDocumentRelatedEntityType.ENTRY_SUMMARY,
    );
    const hasPunAttachedDocument: boolean = !!attachedDocuments.find(
      (doc) =>
        doc.relatedEntityId === entrySummary.id &&
        doc.type === enumDocumentType.ACH_APPROVAL &&
        doc.relatedEntityType === enumDocumentRelatedEntityType.ENTRY_SUMMARY,
    );
    const result: ValidationMessageModel[] = [];
    if (!!notUploadedDocuments?.length) {
      result.push({
        text: SCAN_FAIL_FILE,
        severity: enumValidationMessageSeverity.ERROR,
      });
    }
    if (!!invalidAfterScanDocuments?.length) {
      result.push({
        text: FILE_SIZE_TO_BIG_AFTER_SCAN,
        severity: enumValidationMessageSeverity.ERROR,
      });
    }

    if (entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL) {
      if (entrySummary.bondRequest && entrySummary.bondRequest.state === enumBondRequestState.CREATED) {
        if (!hasPoaAttachedDocument) {
          result.push({
            text: MISSING_ATTACHED_DOCUMENT_POA,
            severity: enumValidationMessageSeverity.ERROR,
            params: { relatedEntityId: entrySummary.id },
          });
        }
      }

      return result;
    }

    if (
      entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH &&
      !hasPunAttachedDocument
    ) {
      result.push({
        text: MISSING_ATTACHED_DOCUMENT_PUN,
        severity: enumValidationMessageSeverity.ERROR,
        params: { relatedEntityId: entrySummary.id },
      });
    }

    if (!hasPoaAttachedDocument) {
      result.push({
        text: MISSING_ATTACHED_DOCUMENT_POA,
        severity: enumValidationMessageSeverity.ERROR,
        params: { relatedEntityId: entrySummary.id },
      });
    }

    for (const shipment of entrySummary.shipments || []) {
      const shipmentHasAttachedDocument: boolean = !!attachedDocuments.find(
        (doc) =>
          doc.relatedEntityId === shipment.id &&
          doc.type === enumDocumentType.BILL_OF_LADING &&
          doc.relatedEntityType === enumDocumentRelatedEntityType.SHIPMENT,
      );
      if (!shipmentHasAttachedDocument && entrySummary.transportationMode !== enumTransportationMode.TRUCK) {
        result.push({
          text: MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING,
          severity: enumValidationMessageSeverity.ERROR,
          params: { relatedEntityId: shipment.id },
        });
      }

      for (const invoice of shipment.invoices || []) {
        const invoiceHasAttachedDocument: boolean = !!attachedDocuments.find(
          (doc) =>
            doc.relatedEntityId === invoice.id &&
            doc.type === enumDocumentType.INVOICE &&
            doc.relatedEntityType === enumDocumentRelatedEntityType.INVOICE,
        );
        if (!invoiceHasAttachedDocument) {
          result.push({
            text: MISSING_ATTACHED_DOCUMENT_INVOICE,
            severity: enumValidationMessageSeverity.ERROR,
            params: { relatedEntityId: invoice.id },
          });
        }
      }
    }
    return result;
  }

  private getDutiesPaymentTypeMessages(
    entrySummary: EntrySummaryModel,
    company?: CompanyModel | ManagementCompanyModel,
  ): ValidationMessageModel[] {
    if (!company) {
      company = this.companiesService.getCompany();
    }

    if (
      entrySummary.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.INSTANT ||
      (company?.authorizationAgreementAccepted && company?.payerUnitNumber)
    ) {
      return [];
    }
    if (!company?.authorizationAgreementAccepted && !company?.payerUnitNumber) {
      return [
        {
          text: ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN,
          severity: enumValidationMessageSeverity.ERROR,
        },
      ];
    }
    return [
      {
        text: ACH_METHOD_NOT_ALLOWED_WITH_PUN,
        severity: enumValidationMessageSeverity.ERROR,
      },
    ];
  }

  private getAttachedDocumentMessages(entrySummary: EntrySummaryModel, attachedDocuments: DocumentModel[]): ValidationMessageModel[] {
    if (
      [enumEntrySummaryState.ACCEPTED, enumEntrySummaryState.NO_BILL_ON_FILE, enumEntrySummaryState.APPROVED].includes(
        entrySummary.state,
      ) ||
      (entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
        !this.brokerValidationStates.includes(entrySummary.state))
    ) {
      return [];
    }
    return this.getRequiredDocumentMessages(entrySummary, attachedDocuments);
  }

  private getNotUploadedDocuments(documents: DocumentModel[]): DocumentModel[] {
    return documents.filter(
      (document: DocumentModel) => ![enumDocumentState.UPLOADED, enumDocumentState.FILE_SIZE_TO_BIG_AFTER_SCAN].includes(document.state),
    );
  }

  private getInvalidAfterScanDocuments(documents: DocumentModel[]): DocumentModel[] {
    return documents.filter((document: DocumentModel) => [enumDocumentState.FILE_SIZE_TO_BIG_AFTER_SCAN].includes(document.state));
  }

  private getImporterMessages(entrySummary: EntrySummaryModel): ValidationMessageModel[] {
    if (entrySummary.importer?.validationMessages?.length > 0) {
      return entrySummary.importer?.validationMessages;
    } else {
      return [];
    }
  }

  private getShipmentTypeMessages(entrySummary: EntrySummaryModel, totalEnteredValue: number): ValidationMessageModel[] {
    if (
      entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      !this.brokerValidationStates.includes(entrySummary.state)
    ) {
      return [];
    }
    const result: ValidationMessageModel[] = [];
    const allLineItems: LineModel[] = this.getAllLines(entrySummary);
    const isHasChina: boolean = allLineItems.map((line) => line.countryOfOrigin?.code).some((code) => code === CHINA_COUNTRY_CODE);
    const limit: number = isHasChina ? ENTERED_VALUE_CHINA_LIMIT : ENTERED_VALUE_TOTAL_LIMIT;
    // #13284: an ANI (CBP Assigned Number) importer must file Formal at any goods value, so the
    // low-value warning must not fire for it — it is severity-driven and would block payment.
    if (
      entrySummary.type === enumEntrySummaryType.FORMAL &&
      !isCbpAssignedNumberImporter(entrySummary.importer) &&
      totalEnteredValue <= limit
    ) {
      result.push({
        text: FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL,
        severity: enumValidationMessageSeverity.WARNING,
        params: { limit, totalEnteredValue },
      });
    }
    if (entrySummary.type === enumEntrySummaryType.INFORMAL && totalEnteredValue > limit) {
      const isNonDraft = entrySummary.state !== enumEntrySummaryState.NOT_SENT;
      result.push({
        text:
          isHasChina && totalEnteredValue > ENTERED_VALUE_CHINA_LIMIT && totalEnteredValue <= ENTERED_VALUE_TOTAL_LIMIT
            ? INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG
            : isNonDraft
            ? INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG
            : INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG,
        severity:
          entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
            ? enumValidationMessageSeverity.WARNING
            : isHasChina && totalEnteredValue > ENTERED_VALUE_CHINA_LIMIT && totalEnteredValue <= ENTERED_VALUE_TOTAL_LIMIT
            ? enumValidationMessageSeverity.WARNING
            : enumValidationMessageSeverity.ERROR,
        params: { limit, totalEnteredValue },
      });
    }

    return result;
  }

  private getAllLines(entrySummary: EntrySummaryModel): LineModel[] {
    const allShipments: ShipmentModel[] = entrySummary.shipments || [];
    const allInvoices: InvoiceModel[] = flatten(allShipments.map((shipment) => shipment.invoices || []));
    return flatten(allInvoices.map((invoice) => invoice.lines || []));
  }

  private getShipmentValidationMessagesErrorData(
    shipment: ShipmentModel,
    transportationMode: enumTransportationMode = enumTransportationMode.OCEAN,
  ): Observable<ValidationMessageModel[]> {
    return this.entrySummaryService.getShipmentValidationMessagesErrorData(shipment, transportationMode);
  }

  private getValidationErrorLinkData(error: ValidationMessageModel, entrySummary: EntrySummaryModel): IEsValidationData {
    let editPageType: enumEsEditPageType;
    const data: IEsValidationDataFields = {
      referenceNumber: entrySummary.referenceNumber,
    };
    const errorMessage = UtilsHelper.lastElement<string>(error.text.split('.'));
    const shipmentByInvoiceError = this.findShipmentByInvoiceError(errorMessage, entrySummary.shipments);
    const shipmentByManufacturerError = this.findShipmentByManufacturerError(errorMessage, entrySummary.shipments);
    const shipmentByManufacturerAddressError = this.findShipmentByManufacturerAddressError(errorMessage, entrySummary.shipments);
    switch (error.text) {
      case MISSING_ATTACHED_DOCUMENT_BILL_OF_LADING:
      case MISSING_ATTACHED_DOCUMENT_INVOICE:
      case MISSING_ATTACHED_DOCUMENT_POA:
      case MISSING_ATTACHED_DOCUMENT_PUN:
      case SCAN_FAIL_FILE:
      case FILE_SIZE_TO_BIG_AFTER_SCAN:
        editPageType = enumEsEditPageType.ATTACHED_DOCUMENTS;
        break;

      case TRANSPORTATION_MODE_MISSING:
        editPageType = enumEsEditPageType.TRANSPORTATION_MODE;
        break;

      case HTS_NOT_VERIFIED:
        editPageType = enumEsEditPageType.HTS_VALIDATION;
        break;

      case ARRIVAL_DATE_MISSING:
      case ARRIVAL_DATE_TOO_EARLY:
      case LOCATION_MISSING:
      case PORT_OF_ENTRY_MISSING:
      case PORT_OF_UNLADING_MISSING:
      case TRIP_MISSING:
      case FLIGHT_NUMBER_MISSING:
      case VESSEL_NAME_MISSING:
      case VESSEL_NAME_TOO_LONG:
      case PAYER_UNIT_NUMBER_MISSING:
      case PAYER_UNIT_NUMBER_MALFORMED:
      case STATE_OF_DESTINATION_MISSING:
      case FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL:
      case INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG:
      case INFORMAL_NON_DRAFT_ES_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG:
      case FORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_SMALL:
      case INFORMAL_SHIPMENT_TYPE_CN_ENTERED_VALUE_TOO_BIG:
      case BOND_REQUEST_NOT_ALLOWED_FOR_INFORMAL_ENTRY:
      case FORMAL_ENTRY_REQUIRED_FOR_CBP_ASSIGNED_NUMBER:
      case ACH_METHOD_NOT_ALLOWED_WITH_PUN:
      case ACH_METHOD_NOT_ALLOWED_WITHOUT_PUN:
        editPageType = enumEsEditPageType.SHIPMENT_DATA;
        break;

      case SHIPMENT_MISSING:
        editPageType = enumEsEditPageType.BILL_OF_LADING;
        break;

      case BOND_SURETY_CODE_MISSING:
      case BOND_REFERENCE_NUMBER_MALFORMED:
      case BOND_TYPE_MISSING:
      case BOND_REQUEST_PORT_CODE_MISSING:
      case BOND_REQUEST_AMOUNT_MISSING:
      case SINGLE_BOND_REQUEST_FORBIDDEN:
      case BOND_REQUEST_NOT_SENT:
        editPageType = enumEsEditPageType.BOND;
        break;

      case `SHIPMENTS.${EXPORT_DATE_MISSING}`:
      case `SHIPMENTS.${EXPORT_DATE_LATER_THAN_ARRIVAL_DATE}`:
      case `SHIPMENTS.${FOREIGN_PORT_MISSING}`:
        if (!entrySummary.hasSingleExportData) {
          editPageType = enumEsEditPageType.SHIPMENT_DATA;
        } else {
          editPageType = enumEsEditPageType.BILL_OF_LADING;
          data.shipmentId = this.findIdByError(errorMessage, entrySummary.shipments);
        }
        break;

      case `SHIPMENTS.${IT_DATE_MISSING}`:
      case `SHIPMENTS.${IT_NUMBER_MISSING}`:
      case `SHIPMENTS.${IT_NUMBER_MALFORMED}`:
      case `SHIPMENTS.${IT_DATE_LATER_THAN_ENTRY_DATE}`:
      case `SHIPMENTS.${FREIGHT_COST_MISSING}`:
      case `SHIPMENTS.${MASTER_BILL_NUMBER_MISSING}`:
      case `SHIPMENTS.${MASTER_AIR_BILL_NUMBER_MISSING}`:
      case `SHIPMENTS.${MASTER_BILL_CARRIER_MISSING}`:
      case `SHIPMENTS.${MASTER_AIR_BILL_PREFIX_MISSING}`:
      case `SHIPMENTS.${MASTER_BILL_CARRIER_WRONG_LENGTH}`:
      case `SHIPMENTS.${MASTER_AIR_BILL_PREFIX_WRONG_LENGTH}`:
      case `SHIPMENTS.${MASTER_BILL_TOO_LONG}`:
      case `SHIPMENTS.${MASTER_AIR_BILL_NUMBER_TOO_LONG}`:
      case `SHIPMENTS.${HOUSE_BILL_CARRIER_MISSING}`:
      case `SHIPMENTS.${HOUSE_AIR_BILL_PREFIX_MISSING}`:
      case `SHIPMENTS.${HOUSE_BILL_NUMBER_MISSING}`:
      case `SHIPMENTS.${HOUSE_AIR_BILL_NUMBER_MISSING}`:
      case `SHIPMENTS.${HOUSE_BILL_CARRIER_WRONG_LENGTH}`:
      case `SHIPMENTS.${HOUSE_AIR_BILL_PREFIX_WRONG_LENGTH}`:
      case `SHIPMENTS.${HOUSE_BILL_TOO_LONG}`:
      case `SHIPMENTS.${HOUSE_AIR_BILL_NUMBER_TOO_LONG}`:
      case `SHIPMENTS.${MASTER_BILL_CARRIER_MALFORMED}`:
      case `SHIPMENTS.${MASTER_BILL_NUMBER_MALFORMED}`:
      case `SHIPMENTS.${HOUSE_BILL_CARRIER_MALFORMED}`:
      case `SHIPMENTS.${HOUSE_BILL_NUMBER_MALFORMED}`:
      case `SHIPMENTS.${QUANTITY_MISSING}`:
      case `SHIPMENTS.${QUANTITY_UNIT_MISSING}`:
        editPageType = enumEsEditPageType.BILL_OF_LADING;
        data.shipmentId = this.findIdByError(EntrySummaryValidationHelper.replaceAirTextError(errorMessage), entrySummary.shipments);
        break;

      case `SHIPMENTS.${SELLER_MISSING}`:
        editPageType = enumEsEditPageType.SHIPMENT_SELLER;
        data.shipmentId = this.findIdByError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.SELLER.${MISSING_COMPANY_NAME}`:
      case `SHIPMENTS.SELLER.${MISSING_ADDRESS}`:
      case `SHIPMENTS.SELLER.${COMPANY_NAME_MALFORMED}`:
        editPageType = enumEsEditPageType.SHIPMENT_SELLER;
        data.shipmentId = this.findIdBySellerError(errorMessage, entrySummary.shipments);

        break;

      case `SHIPMENTS.SELLER.ADDRESS.${MISSING_COUNTRY}`:
      case `SHIPMENTS.SELLER.ADDRESS.${MISSING_CITY}`:
      case `SHIPMENTS.SELLER.ADDRESS.${CITY_MALFORMED}`:
      case `SHIPMENTS.SELLER.ADDRESS.${MISSING_ADDRESS}`:
      case `SHIPMENTS.SELLER.ADDRESS.${POSTAL_CODE_MALFORMED}`:
        editPageType = enumEsEditPageType.SHIPMENT_SELLER;
        data.shipmentId = this.findShipmentIdBySellerAddressError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.${IMPORTER_MISSING}`:
        editPageType = enumEsEditPageType.IMPORTER;
        data.shipmentId = this.findIdByError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.IMPORTER.${MISSING_IDENTIFIER_QUALIFIER}`:
      case `SHIPMENTS.IMPORTER.${MISSING_IDENTIFIER}`:
      case `SHIPMENTS.IMPORTER.${MISSING_DATE_OF_BIRTH}`:
      case `SHIPMENTS.IMPORTER.${WRONG_IDENTIFIER_LENGTH}`:
      case `SHIPMENTS.IMPORTER.${WRONG_IDENTIFIER_FORMAT}`:
      case `SHIPMENTS.IMPORTER.${COMPANY_NAME_MALFORMED}`:
      case `SHIPMENTS.IMPORTER.${QUALIFIER_MALFORMED}`:
      case `SHIPMENTS.IMPORTER.${IDENTIFIER_MALFORMED}`:
      case `SHIPMENTS.IMPORTER.ADDRESS.${MISSING_ADDRESS}`:
      case `SHIPMENTS.IMPORTER.ADDRESS.${MISSING_COUNTRY}`:
      case `SHIPMENTS.IMPORTER.ADDRESS.${MISSING_CITY}`:
      case `SHIPMENTS.IMPORTER.ADDRESS.${CITY_MALFORMED}`:
      case `SHIPMENTS.IMPORTER.ADDRESS.${POSTAL_CODE_MALFORMED}`:
      case `SHIPMENTS.IMPORTER.ADDRESS.${STREET_ADDRESS_MALFORMED}`:
        editPageType = enumEsEditPageType.IMPORTER;
        data.shipmentId = this.findIdByImporterError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.${CONSIGNEE_MISSING}`:
        editPageType = enumEsEditPageType.SHIPMENT_CONSIGNEE;
        data.shipmentId = this.findIdByError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.CONSIGNEE.${MISSING_IDENTIFIER_QUALIFIER}`:
      case `SHIPMENTS.CONSIGNEE.${MISSING_IDENTIFIER}`:
      case `SHIPMENTS.CONSIGNEE.${MISSING_COMPANY_NAME}`:
      case `SHIPMENTS.CONSIGNEE.${COMPANY_NAME_MALFORMED}`:
      case `SHIPMENTS.CONSIGNEE.${QUALIFIER_MALFORMED}`:
      case `SHIPMENTS.CONSIGNEE.${IDENTIFIER_MALFORMED}`:
      case `SHIPMENTS.CONSIGNEE.${WRONG_IDENTIFIER_FORMAT}`:
      case `SHIPMENTS.CONSIGNEE.ADDRESS.${MISSING_COUNTRY}`:
      case `SHIPMENTS.CONSIGNEE.ADDRESS.${MISSING_CITY}`:
      case `SHIPMENTS.CONSIGNEE.ADDRESS.${MISSING_ADDRESS}`:
      case `SHIPMENTS.CONSIGNEE.ADDRESS.${CITY_MALFORMED}`:
      case `SHIPMENTS.CONSIGNEE.ADDRESS.${POSTAL_CODE_MALFORMED}`:
      case `SHIPMENTS.CONSIGNEE.ADDRESS.${STREET_ADDRESS_MALFORMED}`:
        editPageType = enumEsEditPageType.SHIPMENT_CONSIGNEE;
        data.shipmentId = this.findIdByConsigneeError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.${INVOICE_MISSING}`:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE;
        data.shipmentId = this.findIdByError(errorMessage, entrySummary.shipments);
        break;

      case `SHIPMENTS.INVOICES.${INVOICE_NUMBER_MISSING}`:
      case `SHIPMENTS.INVOICES.${INVOICE_MANUFACTURER_MISSING}`:
      case `SHIPMENTS.INVOICES.${INVOICE_NUMBER_MALFORMED}`:
      case `SHIPMENTS.INVOICES.${INVOICE_OCR_STATE_NOT_REVIEWED}`:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE;
        data.shipmentId = shipmentByInvoiceError.id;
        data.invoiceId = this.findIdByError(errorMessage, shipmentByInvoiceError.invoices);
        break;

      case `SHIPMENTS.INVOICES.MANUFACTURER.${MISSING_COMPANY_NAME}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.${MISSING_CANADA_STATE}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.${MISSING_ADDRESS}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.${COMPANY_NAME_MALFORMED}`:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE;
        data.shipmentId = shipmentByManufacturerError.id;
        data.invoiceId = shipmentByManufacturerError.invoices?.find(
          (invoice) => !!this.findIdByError(errorMessage, [invoice.manufacturer]),
        )?.id;
        break;

      case `SHIPMENTS.INVOICES.MANUFACTURER.ADDRESS.${MISSING_COUNTRY}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.ADDRESS.${MISSING_CITY}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.ADDRESS.${MISSING_ADDRESS}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.ADDRESS.${CITY_MALFORMED}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.ADDRESS.${POSTAL_CODE_MALFORMED}`:
      case `SHIPMENTS.INVOICES.MANUFACTURER.ADDRESS.${STREET_ADDRESS_MALFORMED}`:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE;
        data.shipmentId = shipmentByManufacturerAddressError.id;
        data.invoiceId = shipmentByManufacturerAddressError.invoices.find(
          (invoice) => !!this.findIdByAddressError(errorMessage, [invoice.manufacturer]),
        ).id;
        break;

      case CURRENCY_IS_NOT_AVAILABLE:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE;
        data.shipmentId = error.params.shipmentId;
        data.invoiceId = error.params.invoiceId;
        break;

      case `SHIPMENTS.INVOICES.${LINES_MISSING}`:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE_LINE;
        data.shipmentId = shipmentByInvoiceError.id;
        data.invoiceId = this.findIdByError(errorMessage, shipmentByInvoiceError.invoices);
        break;

      case `SHIPMENTS.INVOICES.LINES.${HTS_NUMBER_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${HTS_DOES_NOT_EXIST}`:
      case `SHIPMENTS.INVOICES.LINES.${HTS_NUMBER_WRONG_LENGTH}`:
      case `SHIPMENTS.INVOICES.LINES.${HTS_COUNTRY_OF_ORIGIN_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${GROSS_WEIGHT_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${QUANTITY1_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${QUANTITY2_MALFORMED}`:
      case `SHIPMENTS.INVOICES.LINES.${QUANTITY3_MALFORMED}`:
      case `SHIPMENTS.INVOICES.LINES.${UNIT1_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${VALUE_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${SPECIAL_CHAPTER_MISSING}`:
      case `SHIPMENTS.INVOICES.LINES.${SPECIAL_CHAPTER_HTS_DOES_NOT_EXIST}`:
      case `SHIPMENTS.INVOICES.LINES.${COUNTRY_OF_EXPORT_MISSING}`:
        editPageType = enumEsEditPageType.SHIPMENT_INVOICE_LINE;
        const shipmentByLine = this.findShipmentByLineError(errorMessage, entrySummary.shipments);
        data.shipmentId = shipmentByLine?.id;
        const invoiceByLine = shipmentByLine.invoices?.length ? this.findInvoiceByLineError(errorMessage, shipmentByLine.invoices) : null;
        data.invoiceId = invoiceByLine?.id;
        data.lineId = invoiceByLine?.lines?.length ? this.findIdByError(errorMessage, invoiceByLine.lines) : null;
        break;

      default:
        break;
    }
    return {
      editPageType,
      data,
    };
  }

  private findIdByConsigneeError(
    messageText: string,
    objList: Array<{ validationMessages: ValidationMessageModel[]; id: number; consignee: EntityModel }>,
  ) {
    const obj = objList.find((item) => !!item?.consignee?.validationMessages?.find((message) => message.text === messageText));
    return obj?.id;
  }

  private findIdByImporterError(
    messageText: string,
    objList: Array<{ validationMessages: ValidationMessageModel[]; id: number; importer: EntityModel }>,
  ) {
    const obj = objList.find((item) => !!item?.importer?.validationMessages?.find((message) => message.text === messageText));
    return obj?.id;
  }

  private findIdBySellerError(
    messageText: string,
    objList: Array<{ validationMessages: ValidationMessageModel[]; id: number; seller: EntityModel }>,
  ) {
    const obj = objList.find((item) => !!item?.seller?.validationMessages?.find((message) => message.text === messageText));
    return obj?.id;
  }

  private findShipmentByInvoiceError(messageText: string, shipments: ShipmentModel[]): ShipmentModel {
    for (const shipment of shipments) {
      if (shipment.invoices && shipment.invoices.length) {
        const id = this.findIdByError(messageText, shipment.invoices);
        if (id) {
          return shipment;
        }
      }
    }

    return null;
  }

  private findShipmentByManufacturerError(messageText: string, shipments: ShipmentModel[]): ShipmentModel {
    for (const shipment of shipments) {
      if (shipment.invoices && shipment.invoices.length) {
        const invoice = this.findInvoiceByManufacturerError(messageText, shipment.invoices);
        if (invoice) {
          return shipment;
        }
      }
    }

    return null;
  }

  private findInvoiceByManufacturerError(messageText: string, invoices: InvoiceModel[]) {
    for (const invoice of invoices) {
      if (invoice.manufacturer) {
        const id = this.findIdByError(messageText, [invoice.manufacturer]);
        if (id) {
          return invoice;
        }
      }
    }

    return null;
  }

  private findShipmentIdBySellerAddressError(messageText: string, shipments: ShipmentModel[]): number {
    for (const shipment of shipments) {
      const id = this.findIdByAddressError(messageText, [shipment.seller]);
      if (id) {
        return shipment.id;
      }
    }

    return null;
  }

  private findShipmentByManufacturerAddressError(messageText: string, shipments: ShipmentModel[]): ShipmentModel {
    for (const shipment of shipments) {
      if (shipment.invoices && shipment.invoices.length) {
        const id = this.findIdByAddressError(
          messageText,
          shipment.invoices.map((invoice) => invoice.manufacturer),
        );
        if (id) {
          return shipment;
        }
      }
    }

    return null;
  }

  private findShipmentByLineError(messageText: string, shipments: ShipmentModel[]) {
    for (const shipment of shipments) {
      if (shipment.invoices && shipment.invoices.length) {
        const invoice = this.findInvoiceByLineError(messageText, shipment.invoices);
        if (invoice) {
          return shipment;
        }
      }
    }

    return null;
  }

  private findInvoiceByLineError(messageText: string, invoices: InvoiceModel[]) {
    for (const invoice of invoices) {
      if (invoice.lines && invoice.lines.length) {
        const id = this.findIdByError(messageText, invoice.lines);
        if (id) {
          return invoice;
        }
      }
    }

    return null;
  }

  private findIdByAddressError(messageText: string, objList: Array<{ address: AddressModel; id: number }>) {
    const obj = objList.find(
      (item) =>
        item?.address?.validationMessages &&
        !!Object.values(item.address.validationMessages).find((message: any) => message.text === messageText),
    );
    return obj?.id;
  }

  private findIdByError(messageText: string, objList: Array<{ validationMessages: ValidationMessageModel[]; id: number }>) {
    const obj = objList.find((item) => !!item?.validationMessages?.find((message) => message.text === messageText));
    return obj?.id;
  }

  private getEntityFromError(entityId: number, object: any) {
    if (object.id === entityId) {
      return object;
    }
    for (const field in object) {
      if (object[field] && typeof object[field] === 'object') {
        const entity = this.getEntityFromError(entityId, object[field]);
        if (entity) {
          return entity;
        }
      }
    }

    return null;
  }
}
